import { useInView } from "react-intersection-observer";
import EmptyMessage from "./EmptyMessage";
import FetchMore from "./FetchMore";
import Loader from "./Loader";
import { twMerge } from "tailwind-merge";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { SortDirection } from "global";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import SelectBox from "./SelectBox";

interface WrapperProps {
  children: React.ReactNode;
  className?: string;
}

function Wrapper({ children, className }: WrapperProps) {
  return (
    <div
      className={twMerge(
        "flex h-full max-h-full max-w-full flex-col overflow-y-auto",
        className
      )}
    >
      {children}
    </div>
  );
}

interface HeaderProps<K extends string> {
  tabs: {
    label: string;
    sortKey?: K;
    className?: string;
  }[];
  sort?: {
    key: K;
    direction: SortDirection;
    onSort: (key?: K, direction?: SortDirection) => void;
  };
}

function Header<K extends string>({ tabs, sort }: HeaderProps<K>) {
  return (
    <div className="flex w-full items-center justify-between rounded-t-lg border border-slate-700 bg-slate-700 py-2">
      {tabs.map((tab, index) => {
        const isSortable = !!tab.sortKey;
        const isSorted = sort?.key === tab.sortKey;
        const sortDirection = isSorted ? sort?.direction : null;
        const onSort = sort?.onSort;

        const onSortIconPress = () => {
          if (!onSort || !tab?.sortKey) {
            return;
          }

          if (sortDirection === "ASC") {
            onSort(tab.sortKey, "DESC");
          } else {
            onSort(tab.sortKey, "ASC");
          }
        };

        return (
          <p
            onClick={onSortIconPress}
            className={twMerge(
              "flex w-full items-center justify-center whitespace-nowrap px-2 text-sm font-bold text-white",
              isSortable &&
                "cursor-pointer transition-colors duration-300 hover:text-zinc-300",
              tab?.className
            )}
            key={tab.label + index}
          >
            {tab.label}
            {isSortable && (
              <ChevronUpDownIcon className="h-4 w-4 cursor-pointer text-zinc-300" />
            )}
          </p>
        );
      })}
    </div>
  );
}

interface RowProps {
  children: React.ReactNode;
  isSelected?: boolean;
  onClick?: () => void;
}

function Row({ children, isSelected = false, onClick }: RowProps) {
  const isReadyToBeClicked = !!onClick && !isSelected;

  return (
    <div
      className={twMerge(
        "flex w-full justify-between border-b border-zinc-300 py-3",
        isReadyToBeClicked &&
          "cursor-pointer transition-colors duration-300 hover:bg-zinc-100",
        isSelected && "bg-zinc-100 shadow-inner"
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

interface RowManageButtonsProps {
  onUpdate?: () => void;
  onDelete?: () => void;
  className?: string;
}

function RowManageButtons({
  onDelete,
  onUpdate,
  className,
}: RowManageButtonsProps) {
  if (!onUpdate && !onDelete) {
    return (
      <Column className={`${className} text-zinc-500`}>(관리 불가)</Column>
    );
  }

  return (
    <Column className={className}>
      {!!onUpdate && (
        <button
          onClick={(event) => {
            event.stopPropagation();

            if (onUpdate) {
              onUpdate();
            }
          }}
          className="cursor-pointer whitespace-nowrap font-semibold hover:text-blue-500"
        >
          수정
        </button>
      )}
      {!!onDelete && !!onUpdate && <span className="text-zinc-500">/</span>}
      {!!onDelete && (
        <button
          onClick={(event) => {
            event.stopPropagation();

            if (onDelete) {
              onDelete();
            }
          }}
          className="cursor-pointer whitespace-nowrap font-semibold hover:text-red-500"
        >
          삭제
        </button>
      )}
    </Column>
  );
}

interface ColumnProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  className?: string;
}

function Column({ children, fallback = "-", className = "" }: ColumnProps) {
  return (
    <div
      className={twMerge(
        "flex w-full max-w-full items-center justify-center gap-1 whitespace-nowrap border-e px-2 text-sm",
        className
      )}
    >
      {children || fallback}
    </div>
  );
}

interface BodyProps {
  children?: React.ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  infiniteScroll?: {
    isFetchingMore: boolean;
    hasNextPage: boolean;
    onViewChange: (inView: boolean) => void;
  };
}

function Body({
  children,
  isLoading,
  isEmpty,
  emptyMessage,
  infiniteScroll,
}: BodyProps) {
  const [fetchMoreRef] = useInView({
    threshold: 0.3,
    onChange: infiniteScroll?.onViewChange,
  });

  const isInfiniteScroll = !!infiniteScroll;

  if (isLoading) {
    return (
      <div className="h-full w-full border">
        <Loader />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <EmptyMessage message={emptyMessage} />
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto border border-t-0 border-zinc-300 bg-white">
      {children}
      {isInfiniteScroll && (
        <>
          {infiniteScroll.isFetchingMore && (
            <div className="p-[20px]">
              <Loader />
            </div>
          )}
          {infiniteScroll.hasNextPage && (
            <FetchMore fetchMoreRef={fetchMoreRef} />
          )}
        </>
      )}
    </div>
  );
}

interface PaginationProps {
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  isMovedSingleStepOnly?: boolean;
}

function Pagination({
  page,
  total,
  isMovedSingleStepOnly = false,
  onPageChange,
}: PaginationProps) {
  const isFirstPage = page === 1;
  const isLastPage = page === total;

  const pageOptions = Array.from({ length: total }, (_, index) => ({
    label: `${index + 1}`,
    value: `${index + 1}`,
  }));

  return (
    <div className="flex items-center justify-center gap-10">
      <ArrowLeftIcon
        className={twMerge(
          "aspect-square h-8 cursor-default rounded-lg px-2 py-1 text-zinc-300",
          !isFirstPage &&
            "cursor-pointer text-zinc-700 transition-colors duration-300 hover:bg-zinc-100"
        )}
        onClick={() => {
          if (!isFirstPage) {
            onPageChange(page - 1);
          }
        }}
      />
      {isMovedSingleStepOnly ? (
        <span className="aspect-square rounded-md border border-zinc-300 px-2 py-1 text-sm font-bold text-zinc-500">
          {page}
        </span>
      ) : (
        <SelectBox
          className="ml-auto h-[30px] w-20 min-w-20"
          value={page.toString()}
          onSelect={(pageInString) => onPageChange(+pageInString)}
          options={pageOptions}
        />
      )}
      <ArrowRightIcon
        className={twMerge(
          "aspect-square h-8 cursor-default rounded-lg px-2 py-1 text-zinc-300",
          !isLastPage &&
            "cursor-pointer text-zinc-700 transition-colors duration-300 hover:bg-zinc-100"
        )}
        onClick={() => {
          if (!isLastPage) {
            onPageChange(page + 1);
          }
        }}
      />
    </div>
  );
}

const Table = {
  Wrapper,
  Header,
  Body,
  Row,
  Column,
  RowManageButtons,
  Pagination,
};

export default Table;
